import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { SupportConversationThread } from '@/lib/supportChat';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, MessageSquareText, RefreshCw } from 'lucide-react';

interface AdminSupportConversationListProps {
  conversations: SupportConversationThread[];
  selectedConversationId?: string | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  onSelectConversation: (conversation: SupportConversationThread) => void;
}

export function AdminSupportConversationList({
  conversations,
  selectedConversationId,
  loading,
  error,
  onRetry,
  onSelectConversation,
}: AdminSupportConversationListProps) {
  const sortedConversations = [...conversations].sort(
    (left, right) => new Date(right.last_message_at).getTime() - new Date(left.last_message_at).getTime()
  );

  if (loading && conversations.length === 0) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
      </div>
    );
  }

  if (error && conversations.length === 0) {
    return (
      <div className="m-4 flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-destructive/30 bg-destructive/5 p-5 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <div>
          <p className="font-medium text-foreground">We couldn’t load support conversations.</p>
          <p className="text-sm text-muted-foreground">Please try again in a moment.</p>
        </div>
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="m-4 flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/70 bg-muted/20 p-6 text-center">
        <MessageSquareText className="h-6 w-6 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground">No support conversations yet.</p>
          <p className="text-sm text-muted-foreground">New user messages will appear here automatically.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-3">
      {sortedConversations.map((conversation) => {

        const isSelected = conversation.id === selectedConversationId;
        const displayName = conversation.profile?.full_name?.trim() || conversation.profile?.email || 'Unnamed user';

        return (
          <button
            key={conversation.id}
            type="button"
            onClick={() => onSelectConversation(conversation)}
            className={cn(
              'w-full rounded-2xl border px-4 py-3 text-left transition-colors',
              isSelected
                ? 'border-emerald-500 bg-emerald-500/10'
                : 'border-border/70 bg-background hover:bg-muted/40'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p className="truncate text-sm font-semibold text-foreground">{displayName}</p>
                <p className="truncate text-xs text-muted-foreground">{conversation.profile?.email || 'No email on file'}</p>
              </div>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full bg-muted px-2 py-1 font-medium text-foreground">#{conversation.site_conversation_ref}</span>
              <span className="capitalize">{conversation.status}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
